import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { LopAgGrid } from '../LopAgGrid';
import { getLendingFeeBreakdown } from '../../api/lendings';
import { messages } from '../../i18n/messages';
import { t } from '../../i18n/t';
import type { Lending, LendingFeeBreakdown, LendingFeeComponent } from '../../types/Lending';
import './LendingFeeExplainModal.css';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';

export interface LendingFeeExplainModalProps {
  lendingId: number;
  onClose: () => void;
}

// A short Hebrew explanation of one attribute value. NOT via t(): an unknown code
// (a new row in LendingFeeCalcTypes) must show its raw code rather than throw.
type Dimension = keyof typeof messages.lendings.explain.values;
function explainValue(dim: Dimension, code: string | null): string {
  if (!code) return '';
  const dict = messages.lendings.explain.values[dim] as Record<string, string>;
  return Object.prototype.hasOwnProperty.call(dict, code)
    ? `${dict[code]} (${code})`
    : t('lendings.explain.method.unknownValue', { code });
}

function fmtDateStr(v: string | null | undefined): string {
  return formatDateOnly(v);
}

function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  // Adding 0 turns IEEE -0 into 0 so a fully-earned prepaid fee does not read "-0.00".
  return (Number(v) + 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPercent(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return `${(Number(v) * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

const dateFmt = (p: ValueFormatterParams<LendingFeeComponent>) => fmtDateStr(p.value as string | null);
const moneyFmt = (p: ValueFormatterParams<LendingFeeComponent>) => fmtMoney(p.value as number | null);
const pctFmt = (p: ValueFormatterParams<LendingFeeComponent>) => fmtPercent(p.value as number | null);

// The fee as the view stores it: SUM(feeSign * componentFee).
const signedFee = (c: LendingFeeComponent) => (c.feeSign ?? 1) * (c.componentFee ?? 0);

export function LendingFeeExplainModal({ lendingId, onClose }: LendingFeeExplainModalProps) {
  const [data, setData] = useState<LendingFeeBreakdown | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    getLendingFeeBreakdown(lendingId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [lendingId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const columnDefs = useMemo<ColDef<LendingFeeComponent>[]>(
    () => [
      { field: 'componentKind', headerName: t('lendings.explain.breakdown.kind'), width: 90 },
      { field: 'fromDate', headerName: t('lendings.explain.breakdown.fromDate'), width: 115, valueFormatter: dateFmt },
      { field: 'toDate', headerName: t('lendings.explain.breakdown.toDate'), width: 115, valueFormatter: dateFmt },
      { field: 'days', headerName: t('lendings.explain.breakdown.days'), width: 80, type: 'numericColumn' },
      { field: 'daysInYear', headerName: t('lendings.explain.breakdown.daysInYear'), width: 95, type: 'numericColumn' },
      { field: 'qty', headerName: t('lendings.explain.breakdown.qty'), width: 120, type: 'numericColumn' },
      { field: 'rateUsed', headerName: t('lendings.explain.breakdown.rateUsed'), width: 100, type: 'numericColumn' },
      { field: 'rateSourceDate', headerName: t('lendings.explain.breakdown.rateSourceDate'), width: 120, valueFormatter: dateFmt },
      { field: 'rateSource', headerName: t('lendings.explain.breakdown.rateSource'), width: 190 },
      { field: 'feePercent', headerName: t('lendings.explain.breakdown.feePercent'), width: 100, valueFormatter: pctFmt, type: 'numericColumn' },
      { field: 'notional', headerName: t('lendings.explain.breakdown.notional'), width: 120, valueFormatter: moneyFmt, type: 'numericColumn' },
      { field: 'feeSign', headerName: t('lendings.explain.breakdown.sign'), width: 80, type: 'numericColumn' },
      {
        colId: 'signedFee',
        headerName: t('lendings.explain.breakdown.componentFee'),
        width: 130,
        type: 'numericColumn',
        valueGetter: (p) => (p.data ? signedFee(p.data) : null),
        valueFormatter: moneyFmt,
      },
    ],
    [],
  );

  const rowData = useMemo(() => data?.components ?? [], [data]);
  const defaultColDef = useMemo<ColDef<LendingFeeComponent>>(() => ({ resizable: true, sortable: true }), []);

  const h: Lending | null = data?.header ?? null;
  const sum = useMemo(() => rowData.reduce((acc, c) => acc + signedFee(c), 0), [rowData]);
  const stored = h?.accruedFee ?? null;
  // Rounded to the agora: LendingAccruals is materialized, so a difference below
  // half an agora is float noise, anything above it means the stored number is stale.
  const stale = stored !== null && rowData.length > 0 && Math.abs(sum - stored) >= 0.005;

  const modal = (
    <div
      className="lfe-modal__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="lfe-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('lendings.explain.title')}
        dir="rtl"
      >
        <header className="lfe-modal__header">
          <h2 className="lfe-modal__title">{t('lendings.explain.title')}</h2>
          <button
            type="button"
            className="lfe-modal__close"
            aria-label={t('lendings.explain.close')}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        {error && <div className="lfe-modal__status">{t('lendings.explain.loadError')}</div>}
        {!error && !h && <div className="lfe-modal__status">{t('lendings.explain.loading')}</div>}

        {h && (
          <div className="lfe-modal__body">
            <section>
              <h3 className="lfe-modal__section-title">{t('lendings.explain.method.heading')}</h3>
              <dl className="lfe-modal__facts">
                <dt>{t('lendings.explain.method.name')}</dt>
                <dd data-testid="lfe-method-name">{h.calcTypeName ?? ''}</dd>
                {h.calcTypeComment && (
                  <>
                    <dt>{t('lendings.explain.method.comment')}</dt>
                    <dd>{h.calcTypeComment}</dd>
                  </>
                )}
                <dt>{t('lendings.explain.method.source')}</dt>
                <dd>
                  {h.calcTypeSource === 'lending'
                    ? t('lendings.explain.method.sourceLending')
                    : t('lendings.explain.method.sourceBank')}
                </dd>
                <dt>{t('lendings.explain.method.chargeTiming')}</dt>
                <dd>{explainValue('chargeTiming', h.chargeTiming)}</dd>
                <dt>{t('lendings.explain.method.rateBasis')}</dt>
                <dd>{explainValue('rateBasis', h.rateBasis)}</dd>
                <dt>{t('lendings.explain.method.dayCountBasis')}</dt>
                <dd>{explainValue('dayCountBasis', h.dayCountBasis)}</dd>
                <dt>{t('lendings.explain.method.yearBasis')}</dt>
                <dd>{explainValue('yearBasis', h.yearBasis)}</dd>
                <dt>{t('lendings.explain.method.calcKind')}</dt>
                <dd>{explainValue('calcKind', h.calcKind)}</dd>
              </dl>
            </section>

            <section>
              <h3 className="lfe-modal__section-title">{t('lendings.explain.data.heading')}</h3>
              <dl className="lfe-modal__facts">
                <dt>{t('lendings.explain.data.qty')}</dt>
                <dd>{h.qty === null ? '' : Number(h.qty).toLocaleString('he-IL')}</dd>
                <dt>{t('lendings.explain.data.startRate')}</dt>
                <dd>{h.startRate ?? ''}</dd>
                <dt>{t('lendings.explain.data.feePercent')}</dt>
                <dd>{fmtPercent(h.feePercent)}</dd>
                <dt>{t('lendings.explain.data.startDate')}</dt>
                <dd>{fmtDateStr(h.startDate)}</dd>
                <dt>{t('lendings.explain.data.endDate')}</dt>
                <dd>{h.endDate ? fmtDateStr(h.endDate) : t('lendings.explain.data.onCall')}</dd>
                <dt>{t('lendings.explain.data.asOfDate')}</dt>
                <dd>{fmtDateStr(h.asOfDate)}</dd>
              </dl>
            </section>

            <section className="lfe-modal__section--grow">
              <h3 className="lfe-modal__section-title">{t('lendings.explain.breakdown.heading')}</h3>
              {rowData.length === 0 ? (
                <div>{t('lendings.explain.breakdown.noRows')}</div>
              ) : (
                <div className="lfe-modal__grid">
                  <LopAgGrid<LendingFeeComponent>
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    enableRtl
                  />
                </div>
              )}
              <div className="lfe-modal__total" data-testid="lfe-total">
                <span>{t('lendings.explain.breakdown.total')}:</span>
                {stored === null ? (
                  <span data-testid="lfe-total-value">{t('lendings.explain.breakdown.unknown')}</span>
                ) : (
                  <span data-testid="lfe-total-value">{fmtMoney(stored)}</span>
                )}
                <span className="lfe-modal__total-note">
                  {stored !== null && stored < 0
                    ? t('lendings.explain.breakdown.signNegative')
                    : stored !== null && stored > 0
                      ? t('lendings.explain.breakdown.signPositive')
                      : ''}
                </span>
                {stale && (
                  <span className="lfe-modal__stale" data-testid="lfe-stale">
                    {t('lendings.explain.breakdown.totalSum')}: {fmtMoney(sum)} -{' '}
                    {t('lendings.explain.breakdown.stale', {
                      builtAt: formatDateTimeIL(h.feeBuiltAt),
                    })}
                  </span>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
